import { CSSProperties, MouseEvent, useCallback, useMemo } from "react";
import { defineMessages, useIntl } from "react-intl";
import Skeleton from "react-loading-skeleton";
import { IconCameraX } from "@tabler/icons-react";
import { AnyPeriod, classNames, toGQLPeriod } from "@ct-react/core";
import { LocaleLink, useLocaleFormatter } from "@ct-react/locale";
import { BookingOption } from "@ct-react/calendar";
import { buildArticleUrl } from "@shared/url";
import { AnyArticleDetails, Image, isRentalArticle, isSaleArticle } from "@shared/graphql";
import { common, pricing } from "../i18n/sharable-defs";
import { Ratio } from "../models/common";
import { DataProps, SkeletonableProps } from "../tools/components";
import { useBookmarksContext } from "../contexts/bookmarks";
import ImagesCarousel from "./medias/images-carousel";
import SpriteIcon from "./medias/sprite-icon";
import "./article-card.scss";

export const card = defineMessages({
  novelty: { id: "cards.tags.novelty", defaultMessage: "Nouveauté" },
  recommended: { id: "cards.tags.recommended", defaultMessage: "Coup de coeur" },
  sold: { id: "cards.tags.sold", defaultMessage: "Vendu" },
  reserved: { id: "cards.tags.reserved", defaultMessage: "Réservé" },
});

type Props = AnyArticleDetails & {
  suggestion?: BookingOption;
  searchExclusivity?: true;
  untargetable?: true;
  withInnerContextNav?: boolean;
};

const Content = ({ data, imageRatio }: DataProps<Props> & { imageRatio: Ratio }) => {

  const intl = useIntl();
  const { print } = useLocaleFormatter();
  const bookmarks = useBookmarksContext();

  const images = useMemo<Image[]>(() => data?.images || [], [ data?.id ]);

  const formatSuggestion = useCallback((opt: BookingOption) => {
    const period = print.period(opt.period, true, true);
    return opt.price === "onDemand"
      ? `${intl.formatMessage(pricing.onDemand)} ${period}`
      : `${print.price(opt.price.amount, { trailingZeroDisplay: "stripIfInteger" })} ${period}`
  }, [ intl ]);

  const onBookmark = (item: AnyArticleDetails, e: MouseEvent) => {
    e.preventDefault();
    bookmarks.toggle(item.__typename, item.id);
  }

  return (
    <>
      <div className="card-pictures"
           style={{ "--ccv-img-ratio": imageRatio } as CSSProperties}>
        {images.length > 0
          ? <ImagesCarousel ratio={imageRatio} data={images} />
          : <IconCameraX className="x-icon" />
        }
        <ul className="card-badges">
          {data.novelty &&
            <li>{intl.formatMessage(card.novelty)}</li>
          }
          {data.recommended &&
            <li>{intl.formatMessage(card.recommended)}</li>
          }
          {(isSaleArticle(data) && data.reserved) &&
            <li>{intl.formatMessage(card.reserved)}</li>
          }
        </ul>
        {!data.untargetable &&
          <span className="bookmark" onClick={onBookmark.bind(this, data)}>
            <SpriteIcon hash={bookmarks.check(data.__typename, data.id) ? "action.bookmarked" : "action.bookmark"} />
          </span>
        }
      </div>

      <div className="card-resume">

        <span className="location">
          {data!.address.city}
          {(isSaleArticle(data) && !!data.features.rooms) &&
            <span className="rooms">
              {intl.formatMessage(common.countedRooms, { count: data.features.rooms })}
            </span>
          }
        </span>

        <h2 className="title">{ data!.title.value }</h2>

        <ul className="features">
          {(isRentalArticle(data) && !!data.features.maxGuests) &&
            <li>
              <SpriteIcon hash="features.person" />
              {intl.formatMessage(common.countedGuests, { count: data.features.maxGuests })}
            </li>
          }
          {!!data.features.bedrooms &&
            <li>
              <SpriteIcon hash="features.bedroom" />
              {intl.formatMessage(common.countedBedrooms, { count: data.features.bedrooms })}
            </li>
          }
          {!!data.features.bathrooms &&
            <li>
              <SpriteIcon hash="features.bathroom" />
              {intl.formatMessage(common.countedBathrooms, { count: data.features.bathrooms })}
            </li>
          }
          {(isSaleArticle(data) && (!!data.features.livingSpace || !!data.features.plotSpace)) &&
            <li>
              <SpriteIcon hash="features.surface" />
              {`${data.features.livingSpace || data.features.plotSpace} m²`}
            </li>
          }
        </ul>

        {!data.untargetable &&
          <>
            {isRentalArticle(data) &&
              <span className="price">
                {!!data.suggestion
                  ? formatSuggestion(data.suggestion)
                  : (data.lowestPrice.onDemand
                    ? intl.formatMessage(pricing.onDemand)
                    : intl.formatMessage(pricing.lowest, {
                      type: data.lowestPrice.type,
                      price: print.price(data.lowestPrice.value, { trailingZeroDisplay: "stripIfInteger" })
                    }))
                }
              </span>
            }

            {isSaleArticle(data) &&
              <span className="price">
                {data.price.onDemand
                  ? intl.formatMessage(pricing.onDemand)
                  : print.price(data.price.value, { trailingZeroDisplay: "stripIfInteger" })
                }
              </span>
            }
          </>
        }

      </div>
    </>);

}

const ArticleCard = (
  {
    loading,
    className,
    data
  }: SkeletonableProps<Props>) => {

  const buildTarget = useCallback((data: AnyArticleDetails, period?: AnyPeriod) => {
    const url = buildArticleUrl(data);
    if (!isRentalArticle(data) || !period) return url;
    const formatted = toGQLPeriod(period);
    return `${url}&b_in=${formatted[0]}&b_out=${formatted[1]}`;
  }, []);

  const contentProps = useMemo(() => ({
    data: data!,
    imageRatio: data?.searchExclusivity ? "3/1" : "3/2" as Ratio
  }), [ data ]);

  const wrapperClasses = classNames("article-card", className, {
    "no-image": !loading && data?.images.length == 0
  });

  if (loading || !data)
    return (
      <article className={wrapperClasses}>
        <Skeleton style={{ aspectRatio: contentProps.imageRatio }} />
        <div className="card-resume">
          <Skeleton className="location" width="30%" />
          <Skeleton className="title" inline={true} />
          <Skeleton className="features" width="65%" />
          <Skeleton className="price" width={175} />
        </div>
      </article>);

  return (
    <article className={wrapperClasses}>
      {!!data.untargetable
          ? (<Content {...contentProps} />)
          : (<LocaleLink to={buildTarget(data, data.suggestion?.period)}
                         state={{...!!data.withInnerContextNav && { innerNav: true }}}>
              <Content {...contentProps} />
            </LocaleLink>)
      }
    </article>);

}

export default ArticleCard;
